/* Géométrie des ouvertures — vérité unique.
   Le moteur 3D (proconex-configurator.js) et le panneau (proconex-cfg-panel.js) décidaient chacun
   de leur côté où se trouvent les ouvertures et si deux d'entre elles se croisent. Le panneau lisait
   même les emprises des portes dans le registre publié par la 3D après un rendu : au tout premier
   affichage, avant que Three.js soit chargé, ce registre n'existait pas et le placement automatique
   croyait le mur vide.

   Ce module calcule les boîtes SYNCHRONEMENT depuis l'état canonique, et porte la règle de collision.
   Les deux fichiers l'utilisent. Chargé en script classique, donc avant le module 3D et avant le
   panneau différé.

   Repère : x le long du mur, en mètres monde, 0 au milieu du mur ; y depuis le sol du bâtiment.
   Les groupes de portes sont tournés par la 3D : leur x local devient −x ou −z monde sur le mur
   arrière et le côté droit. Les fenêtres, elles, sont déjà posées en coordonnées monde. */
(function () {
  "use strict";

  var F = 0.3048;          // pi → m
  var EDGE = 0.28;         // retrait minimal au coin
  var GAP = 0.24;          // jeu minimal entre deux ouvertures
  var EPS = 1e-6;          // tolérance numérique, pas un jeu
  var SLAB = 0.14;         // y0 : épaisseur de dalle, plancher du bâtiment
  var SVC_PO = 34, SVC_H_PO = 84;   // porte de service, validées le 4 septembre
  var WIN_W = 0.86, WIN_H = 0.98;   // vitrage ; l'emprise ajoute le cadre

  var WALL_KEY = { "Avant": "front", "Arrière": "back", "Côté gauche": "left", "Côté droit": "right" };
  var FLIP = { back: true, right: true };   // doorLocalToWorld

  function key(side) { return WALL_KEY[side] || side; }
  function num(v, d) { var n = parseFloat(v); return isFinite(n) ? n : d; }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function wallLen(st, wall) { return ((wall === "front" || wall === "back") ? st.wFt : st.dFt) * F; }
  function wallH(st) { return num(st.wallFt, 8) * F; }
  function toWorld(wall, lx) { return FLIP[wall] ? -lx : lx; }
  function box(id, kind, wall, cx, w, yb, yt) {
    return { id: id, kind: kind, wall: wall, cx: cx, w: w, a: cx - w / 2, b: cx + w / 2, yb: yb, yt: yt };
  }

  /* Règle dure, identique des deux côtés : deux ouvertures se croisent si leurs rectangles se
     recouvrent À LA FOIS horizontalement et verticalement, sur le même mur. Un simple contact bord
     à bord n'en est pas une. Aucune marge n'entre ici : EDGE et GAP servent au placement, jamais à
     décider qu'un emplacement est occupé. */
  function overlaps(p, q) {
    return p.wall === q.wall &&
      p.a < q.b - EPS && q.a < p.b - EPS &&
      p.yb < q.yt - EPS && q.yb < p.yt - EPS;
  }

  /* Portes de garage — 0, 1 ou 2, alignées et espacées de GAP, glissées ensemble par doorPos. */
  function gdoorBoxes(st) {
    var n = clamp(parseInt(st.gdoors, 10) || 0, 0, 2);
    if (!n) return [];
    var wall = key(st.doorSide), wallW = wallLen(st, wall), H = wallH(st);
    var dh = Math.min(H - 0.4, clamp(num(st.gdoorHFt, 7), 6, 12) * F);
    var w = Math.max(0.9, num(st.gdoorWFt, 9) * F);
    var spanTotal = n * w + (n - 1) * GAP;
    var leftEdge = -wallW / 2 + EDGE, rightEdge = wallW / 2 - EDGE;
    var p = clamp(num(st.doorPos, 50), 0, 100);
    var start = leftEdge + Math.max(0, (rightEdge - leftEdge) - spanTotal) * (p / 100) + w / 2;
    var out = [];
    for (var i = 0; i < n; i++) {
      var px = start + i * (w + GAP);
      out.push(box("gdoor" + (i + 1), "gdoor", wall, toWorld(wall, px), w + 0.14, SLAB, SLAB + dh + 0.14));
    }
    return out;
  }

  /* Porte principale d'un cabanon. Deux formes dans le moteur : une porte de garage posée sur un
     cabanon, ou la porte de service/simple/double courante. */
  function mainDoorBox(st) {
    if (st.kind === "garage") return null;
    var wall = key(st.doorSide), wallW = wallLen(st, wall), H = wallH(st);
    var spec = String(st.doorSpec || "");
    var p = clamp(num(st.doorPos, 50), 0, 100);
    if (/garage/i.test(spec)) {
      var gf = num((/([\d.]+)\s*pi/.exec(spec) || [])[1], 8);
      var gw = Math.min(wallW - 2 * EDGE - 0.3, gf * F);
      var gh = Math.min(H - 0.35, clamp(num(st.gdoorHFt, 7), 6, 12) * F);
      var limG = Math.max(0, wallW / 2 - EDGE - (gw + 0.16) / 2);
      var gx = limG * ((p - 50) / 50);
      return box("door", "door", wall, toWorld(wall, gx), gw + 0.16, SLAB, SLAB + gh + 0.16);
    }
    var dw = /double/i.test(spec) ? 1.52 : (/50/.test(spec) ? 1.27 : /34/.test(spec) ? 0.86 : 0.92);
    var dh = Math.min(H - 0.4, 2.03);
    var limD = Math.max(0, wallW / 2 - EDGE - (dw + 0.12) / 2);
    var dcx = limD * ((p - 50) / 50);
    return box("door", "door", wall, toWorld(wall, dcx), dw + 0.12, SLAB, SLAB + dh + 0.12);
  }

  /* Porte de service : pleine hauteur depuis le sol. Son pourcentage court d'un bord à l'autre du
     mur, retraits de coin déduits. */
  function serviceBox(st, side, posPct) {
    var wall = key(side), wallW = wallLen(st, wall), H = wallH(st);
    var sw = (SVC_PO / 12) * F, sh = Math.min(H - 0.3, (SVC_H_PO / 12) * F);
    var run = Math.max(0, wallW - 2 * EDGE - sw);
    var lx = -wallW / 2 + EDGE + sw / 2 + run * (clamp(num(posPct, 85), 0, 100) / 100);
    return box("service", "service", wall, toWorld(wall, lx), sw + 0.12, SLAB, SLAB + sh + 0.12);
  }
  function servicePosFromWorld(st, side, world) {
    var wall = key(side), wallW = wallLen(st, wall);
    var sw = (SVC_PO / 12) * F, run = Math.max(EPS, wallW - 2 * EDGE - sw);
    var lx = FLIP[wall] ? -world : world;
    return clamp(100 * (lx - (-wallW / 2 + EDGE + sw / 2)) / run, 0, 100);
  }

  /* Fenêtre : hauteur d'appui proportionnelle, bornée haut et bas, et pourcentage centré sur le mur
     — sans inversion, le moteur pose les fenêtres directement en coordonnées monde. */
  function windowY(st) {
    var H = wallH(st);
    return SLAB + Math.min(H - WIN_H / 2 - 0.32, Math.max(WIN_H / 2 + 0.62, H * 0.62));
  }
  function windowBox(st, side, posPct, id) {
    var wall = key(side), lim = wallLen(st, wall) / 2 - EDGE, span = lim - WIN_W / 2;
    var cx = -span + 2 * span * (clamp(num(posPct, 50), 0, 100) / 100);
    var y = windowY(st);
    return box(id || "window", "window", wall, cx, WIN_W + 0.12, y - (WIN_H + 0.12) / 2, y + (WIN_H + 0.12) / 2);
  }
  function windowPosFromWorld(st, side, world) {
    var lim = wallLen(st, key(side)) / 2 - EDGE, span = lim - WIN_W / 2;
    if (span <= EPS) return 50;
    return clamp(50 * (1 + world / span), 0, 100);
  }

  /* Toutes les ouvertures STRUCTURELLES d'un état : celles que le placement automatique ne déplace
     jamais. Calculées sans le moindre rendu, donc disponibles dès le premier trait de la page. */
  function structuralBoxes(st) {
    var out = gdoorBoxes(st);
    var d = mainDoorBox(st);
    if (d) out.push(d);
    return out;
  }

  window.ProconexOpenings = {
    F: F, EDGE: EDGE, GAP: GAP, EPS: EPS, SLAB: SLAB, WIN_W: WIN_W, WIN_H: WIN_H,
    SVC_PO: SVC_PO, SVC_H_PO: SVC_H_PO,
    wallKey: key, wallLen: wallLen, wallHeight: wallH, toWorld: toWorld, box: box,
    overlaps: overlaps,
    gdoorBoxes: gdoorBoxes, mainDoorBox: mainDoorBox, structuralBoxes: structuralBoxes,
    serviceBox: serviceBox, servicePosFromWorld: servicePosFromWorld,
    windowBox: windowBox, windowPosFromWorld: windowPosFromWorld, windowY: windowY
  };
})();
